import { Link } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import SEO from '../components/SEO';

const QUALIFICATIONS = [
  { label: 'M. Env. Sci.', institution: 'University of Melbourne', year: '2017' },
  { label: 'Grad Dip. Env Sci & Tech', institution: 'RMIT University', year: '2014' },
  { label: 'B. Design', institution: 'Griffith University', year: '2000' },
];

const EXPERTISE = [
  'Operational Waste Management Plans (DA, CC, CDC)',
  'Demolition & Construction Waste Management Plans',
  'State Significant Development Applications (SSDA & SEARS)',
  'Green Star Credits — 7A, 8A & 22B (Communities)',
  'Section 4.55 Modifications',
  'HAZMAT Surveys & Occupational Hygiene',
  'Waste Audits — Commercial & Industrial',
  'Stormwater & WSUD Strategies',
];

const HIGHLIGHTS = [
  { icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', title: '8+ Years', desc: 'Specialist waste management consulting experience across NSW and VIC' },
  { icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', title: '99+ WMPs', desc: 'Delivered across residential, commercial, hospital, government, and institutional projects' },
  { icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z', title: 'Award Winner', desc: 'Victorian Litter Innovation Fund 2017, Sustainability Victoria' },
  { icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z', title: 'Insured', desc: 'Professional Indemnity and Public Liability insurance' },
];

export default function About() {
  return (
    <PublicLayout>
      <SEO
        title="About — Nathan Smith, Waste Management Plan Consultant"
        description="Nathan Smith is a specialist waste management plan consultant with 8+ years experience, M. Env. Sci. (Melbourne), and a track record of delivering Green Star, SSDA, and DA-compliant WMPs across NSW and VIC."
        keywords="Nathan Smith waste management, waste management consultant Sydney, waste management consultant Melbourne, Green Star waste consultant, SSDA waste management plan, waste audit consultant"
        canonicalPath="/about"
      />

      {/* Hero */}
      <section className="py-16 lg:py-20 bg-[#F8FAFC]" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr_1.5fr] gap-12 items-start">
            {/* Profile card */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgb(0,0,0,0.04)] overflow-hidden" data-testid="profile-card">
              <div className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] p-8 text-center">
                <div className="w-24 h-24 rounded-full bg-white/10 border-2 border-white/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-bold text-white/80">NS</span>
                </div>
                <h1 className="text-xl font-bold text-white" data-testid="consultant-name">Nathan Smith</h1>
                <p className="text-sm text-blue-300 mt-1">Senior Waste Management Consultant</p>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {QUALIFICATIONS.map((q, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#0A2540]">{q.label}</p>
                        <p className="text-xs text-slate-500">{q.institution}, {q.year}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <a href="mailto:nathan.smith@plangenerator.com.au" className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    nathan.smith@plangenerator.com.au
                  </a>
                </div>
              </div>
            </div>

            {/* Bio */}
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">ABOUT THE PRINCIPAL CONSULTANT</div>
              <h2 className="text-2xl lg:text-3xl font-bold tracking-tight text-[#0A2540] leading-tight mb-6">
                Experienced waste management consulting for complex Australian developments
              </h2>
              <div className="prose prose-sm text-slate-600 leading-relaxed space-y-4">
                <p>
                  Nathan Smith is the principal consultant at PlanGen Consulting, bringing over 8 years of specialised experience in waste management planning across New South Wales and Victoria. He has been the leading author on over 99 waste management plans spanning residential, commercial, institutional, government, and entertainment developments.
                </p>
                <p>
                  With qualifications in Environmental Science (University of Melbourne), Environmental Science &amp; Technology (RMIT), and Design (Griffith University), Nathan combines technical environmental knowledge with a design-oriented understanding of how waste systems integrate into building projects.
                </p>
                <p>
                  His project experience spans from standard DA submissions through to complex State Significant Development Applications (SSDA), Green Star certified buildings, and large-scale government projects including hospitals, schools, and public housing. He has worked alongside leading architecture firms, traffic engineers, and sustainability consultants on projects valued from $1M to $500M+.
                </p>
                <p>
                  Nathan is a recipient of the Victorian Litter Innovation Fund (2017, Sustainability Victoria) and has received grant funding from the Commonwealth Government Smart Cities and Suburbs Fund for waste management research.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-12 bg-white border-y border-slate-100" data-testid="highlights">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {HIGHLIGHTS.map((h, i) => (
              <div key={i} className="text-center" data-testid={`highlight-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={h.icon} />
                  </svg>
                </div>
                <p className="text-base font-bold text-[#0A2540]">{h.title}</p>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{h.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section className="py-16 bg-[#F8FAFC]" data-testid="expertise-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">AREAS OF EXPERTISE</div>
              <h2 className="text-2xl font-bold tracking-tight text-[#0A2540] mb-6">Specialist Knowledge</h2>
              <ul className="space-y-3">
                {EXPERTISE.map((e, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-600">
                    <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {e}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">PROJECT SECTORS</div>
              <h2 className="text-2xl font-bold tracking-tight text-[#0A2540] mb-6">Industry Experience</h2>
              <div className="grid grid-cols-2 gap-3">
                {[
                  'Residential & Mixed-Use',
                  'Hospitals & Medical',
                  'Universities & Schools',
                  'Government & Public Housing',
                  'Retail & Commercial',
                  'Entertainment & Theatres',
                  'Industrial & Logistics',
                  'Aged Care Facilities',
                ].map((sector, i) => (
                  <div key={i} className="bg-white rounded-xl border border-slate-100 px-4 py-3 text-sm text-[#0A2540] font-medium">
                    {sector}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0A2540]" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4 tracking-tight">Work with Nathan</h2>
          <p className="text-slate-400 text-base mb-8 max-w-lg mx-auto">Send us your plans and project details for a tailored quote. Most quotes provided within 24 hours.</p>
          <Link to="/contact" className="inline-flex px-8 py-3 rounded-full bg-white text-[#0A2540] font-semibold text-sm hover:bg-slate-100 transition-colors shadow-lg" data-testid="about-cta-btn">
            Request a Quote
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
